import React, { useState, useEffect, useRef } from 'react';
import styles from './FeaturedScenarioCarouselDesktop.module.css';
import { ScenarioDefinitionListResponse } from '../../api/types/scenario';

interface FeaturedScenarioCarouselDesktopProps {
  featuredScenarios: ScenarioDefinitionListResponse[];
  onScenarioClick: (scenario: ScenarioDefinitionListResponse) => void;
}

const FeaturedScenarioCarouselDesktop: React.FC<FeaturedScenarioCarouselDesktopProps> = ({ 
  featuredScenarios = [], // Provide default empty array 
  onScenarioClick 
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const rotationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const resumeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const ROTATION_INTERVAL = 5000; // 5 seconds
  const RESUME_AFTER_INTERACTION = 10000; // Resume auto-rotation after 10 seconds of inactivity
  
  // Add state to track if auto-rotation should be paused
  const [isPaused, setIsPaused] = useState(false);

  // Reset active index if the featured scenarios change
  useEffect(() => {
    if (activeIndex >= featuredScenarios.length && featuredScenarios.length > 0) {
      setActiveIndex(0);
    }
  }, [featuredScenarios, activeIndex]);

  // Add fade-in effect when component mounts
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);

  // Function to pause auto-rotation and set a timeout to resume it
  const pauseAndScheduleResume = () => {
    setIsPaused(true);
    
    // Clear any existing resume timeout
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
    }
    
    // Set a timeout to resume auto-rotation after inactivity
    resumeTimeoutRef.current = setTimeout(() => {
      setIsPaused(false);
    }, RESUME_AFTER_INTERACTION);
  };

  // Auto-rotate the carousel every 5 seconds
  useEffect(() => {
    if (featuredScenarios.length <= 1) return;
    
    // Clear any existing intervals
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    
    if (rotationIntervalRef.current) {
      clearInterval(rotationIntervalRef.current);
    }
    
    // Reset progress
    setProgress(0);
    
    // Only create rotation interval if not paused
    if (!isPaused) {
      // Create rotation interval
      rotationIntervalRef.current = setInterval(() => {
        setActiveIndex((current) => (current + 1) % featuredScenarios.length);
        setProgress(0); // Reset progress when changing slides
      }, ROTATION_INTERVAL);
    }
    
    return () => {
      if (rotationIntervalRef.current) {
        clearInterval(rotationIntervalRef.current);
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
      }
    };
  }, [featuredScenarios.length, isPaused]);

  // Handle manual navigation
  const goToSlide = (index: number) => {
    setActiveIndex(index);
    setProgress(0); // Reset progress when manually changing slides
    pauseAndScheduleResume(); // Pause auto-rotation when user manually navigates
  };

  // Return null if there are no scenarios to display
  if (!featuredScenarios || !featuredScenarios.length) return null;

  // Make sure we have a valid active index
  const safeActiveIndex = Math.min(activeIndex, featuredScenarios.length - 1);
  const activeScenario = featuredScenarios[safeActiveIndex];

  // Additional safety check
  if (!activeScenario) return null;

  // Calculate indices for the carousel cards
  const getVisibleScenarios = () => {
    const result = [];
    const totalScenarios = featuredScenarios.length;
    
    // Show up to 5 cards at a time, regardless of total count
    const maxVisibleCards = 5;
    const maxCards = Math.min(maxVisibleCards, totalScenarios);
    
    for (let i = 0; i < maxCards; i++) {
      // Calculate position relative to active index
      let position = (safeActiveIndex + i - Math.floor(maxCards / 2) + totalScenarios) % totalScenarios;
      result.push({
        scenario: featuredScenarios[position],
        position: i - Math.floor(maxCards / 2) // -2, -1, 0, 1, 2
      });
    }
    
    return result;
  };

  const visibleScenarios = getVisibleScenarios();

  return (
    <div className={`${styles.carouselContainer} ${isVisible ? styles.visible : ''}`}>
      <div className={styles.headerRow}>
        <h2 className={styles.sectionTitle}>Featured Scenarios</h2>
      </div>
      
      <div className={styles.carouselWrapper}>
        {/* Star decorations */}
        <div className={styles.starDecoration1}></div>
        <div className={styles.starDecoration2}></div>
        <div className={styles.starDecoration3}></div>
        <div className={styles.starDecoration4}></div>
        
        {/* Carousel cards */}
        <div className={styles.cardsContainer}>
          {visibleScenarios.map(({ scenario, position }) => (
            <div 
              key={scenario.id} 
              className={`${styles.card} ${position === 0 ? styles.activeCard : ''} ${styles[`position${position}`]}`}
              onClick={() => {
                goToSlide((safeActiveIndex + position + featuredScenarios.length) % featuredScenarios.length);
                if (position === 0) {
                  onScenarioClick(scenario);
                }
              }}
            >
              <div 
                className={styles.cardImage}
                style={{ backgroundImage: `url(${scenario.icon || '/media/default-scenario.png'})` }}
              ></div>
              <div className={styles.cardContent}>
                <h3 className={styles.cardTitle}>{scenario.display_name || 'Untitled'}</h3>
                {position === 0 && (
                  <div className={styles.playButton}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="11" stroke="white" strokeWidth="2"/>
                      <path d="M16 12L10 16V8L16 12Z" fill="white"/>
                    </svg>
                    <span>Start</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {/* Navigation dots */}
        <div className={styles.navigationDots}>
          {featuredScenarios.map((_, index) => (
            <button
              key={index}
              className={`${styles.navDot} ${index === safeActiveIndex ? styles.activeDot : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to scenario ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeaturedScenarioCarouselDesktop; 